import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendingDown, TrendingUp, Minus, Gauge } from 'lucide-react';
import type { FinancialsOutlookResponse } from '@siliconx/shared';
import { FMV_OUTLOOK_COVERAGE_FLOOR_PCT } from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { monthLong } from '@siliconx/shared/ui/FmvOutlookChart';
import { api, throwIfNotOk } from '../../lib/api';
import { formatUsd, formatDate } from '../../lib/format';
import { qk } from '../../lib/queryKeys';
import { ImportFromErpLink } from './ImportFromErpLink';

/**
 * Outlook band (SIL-573 PR 1 of 2, asset-financials screen — see the route
 * file's header). Sits between `CoverageStatCard` and the "By asset" table.
 * Consumes `GET /api/client/financials/outlook`
 * (`loadFinancialsOutlook`, financials-report.ts) — a per-CLASS aggregate
 * forecast, same kernel (`packages/shared/fmv-forecast.ts`) and cone
 * doctrine (`CLIENT_OUTLOOK_CONE_PCT`) as the asset-detail FMV Outlook chart
 * (`ValueTab.tsx` / `FmvOutlookChart`) — visually matched to it (emerald
 * ramp, dashed forecast lines, shaded uncertainty/gap region) so the two
 * forecasts read as the same feature at different grains.
 *
 * Deliberately has NO per-asset drill-down: the aggregate is built from
 * class-level sums, not a per-asset roll-up, so it will never tie out to
 * the "By asset" table below to the dollar — offering a click-through would
 * invite exactly that comparison. This band's own figures are the whole
 * story.
 *
 * EXPORT: this band is never wired into any CSV/PDF export path. The
 * as-of stamp in its header is what keeps a live forecast honest, and an
 * exported document outlives that stamp — asset-financials.tsx passes no
 * `exportCsv` to `FinancialsReportScreen`, and this component must stay
 * that way (no `financials-csv.ts` / `financials-export.ts` wiring for this
 * endpoint). If a future PR adds export to this screen, this band's data
 * must be excluded from it.
 */

const NET_RECOVERY_COLOR = '#047857'; // emerald-700 — matches ValueTab's FMV series color
const BOOK_VALUE_COLOR = '#6ee7b7'; // emerald-300 — matches ValueTab's book reference line

function formatPct(pct: number, opts?: { signed?: boolean }): string {
  const sign = opts?.signed && pct > 0 ? '+' : '';
  return `${sign}${(pct * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------

function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex-1 rounded-lg border border-border bg-muted/30 p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/**
 * `trend.deltaUsd` is the SUM of each contributing asset's own FMV delta
 * over the window (spec "Outlook trend: per-asset delta-sum") — it is the
 * change in FMV of assets that were HELD and PRICED at both ends of the
 * window, NOT portfolio growth (a newly-added or newly-priced asset never
 * contributes its full value here, only its own delta if it qualifies at
 * both ends). The label below says so explicitly — getting that wording
 * unambiguous is the whole point of this figure existing separately from
 * `totalFmvMedUsd`.
 */
export function FmvTotalTile({
  totalFmvMedUsd,
  componentEstimatedAssetCount,
  trend,
}: {
  totalFmvMedUsd: number;
  /** D8 (contract gap G11): servers with no System FMV of their own, valued
   * at the sum of their priced components. */
  componentEstimatedAssetCount: number;
  trend: FinancialsOutlookResponse['trailingTrend'];
}) {
  const Icon = trend === null ? Minus : trend.deltaUsd > 0 ? TrendingUp : trend.deltaUsd < 0 ? TrendingDown : Minus;
  const tone =
    trend === null || trend.deltaUsd === 0
      ? 'text-muted-foreground'
      : trend.deltaUsd > 0
        ? 'text-emerald-600'
        : 'text-amber-700';
  return (
    <Tile label="Portfolio FMV">
      <div className="font-mono text-2xl font-semibold text-foreground">{formatUsd(totalFmvMedUsd)}</div>
      {componentEstimatedAssetCount > 0 && (
        <div className="mt-1 text-xs text-muted-foreground">
          {`Includes ${componentEstimatedAssetCount} ${componentEstimatedAssetCount === 1 ? 'server' : 'servers'} est. from components`}
        </div>
      )}
      <div className={`mt-1.5 flex items-center gap-1 text-xs ${tone}`}>
        <Icon className="size-3.5 shrink-0" />
        {trend === null ? (
          <span>Not enough same-store history yet (90d)</span>
        ) : (
          <span>
            {trend.deltaUsd >= 0 ? '+' : '−'}
            {formatUsd(Math.abs(trend.deltaUsd))}
            {trend.pctChange !== null && ` (${formatPct(Math.abs(trend.pctChange))})`} FMV change, held &amp; priced
            both ends (90d) — not portfolio growth
          </span>
        )}
      </div>
    </Tile>
  );
}

function ProjectionTile({ projected90d }: { projected90d: NonNullable<FinancialsOutlookResponse['projected90d']> }) {
  const negative = projected90d.deltaUsd < 0;
  return (
    <Tile label="90-day projection">
      <div className="font-mono text-2xl font-semibold text-foreground">{formatUsd(projected90d.valueUsd)}</div>
      <div className={`mt-1.5 text-xs ${negative ? 'text-amber-700' : 'text-emerald-600'}`}>
        {negative ? '−' : '+'}
        {formatUsd(Math.abs(projected90d.deltaUsd))} ({formatPct(Math.abs(projected90d.pctChange))}) if held
      </div>
    </Tile>
  );
}

function ActionWindowTile({ refreshCliff }: { refreshCliff: FinancialsOutlookResponse['refreshCliff'] }) {
  const monthsOut = useMemo(() => {
    if (!refreshCliff) return null;
    const now = new Date();
    const [y, m] = refreshCliff.month.split('-').map(Number);
    if (!y || !m) return null;
    const target = new Date(Date.UTC(y, m - 1, 1));
    const months = (target.getUTCFullYear() - now.getUTCFullYear()) * 12 + (target.getUTCMonth() - now.getUTCMonth());
    return months;
  }, [refreshCliff]);

  return (
    <Tile label="Action window">
      {refreshCliff === null || monthsOut === null ? (
        <div className="text-sm text-muted-foreground">No refresh cliff inside the 12-month window.</div>
      ) : monthsOut <= 0 ? (
        <div className="flex items-start gap-1.5 text-sm text-amber-700">
          <Gauge className="mt-0.5 size-3.5 shrink-0" />
          <span>Portfolio has already crossed its earliest 20%-loss point ({monthLong(refreshCliff.month)}).</span>
        </div>
      ) : (
        <div className="flex items-start gap-1.5 text-sm text-amber-700">
          <Gauge className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Projected to lose 20% of current value in ~{monthsOut} month{monthsOut === 1 ? '' : 's'} (
            {monthLong(refreshCliff.month)}).
          </span>
        </div>
      )}
    </Tile>
  );
}

// ---------------------------------------------------------------------------
// Recovery chart — 12 months, projected net recovery vs book value, gap
// shaded (same [lo, hi]-Area idiom FmvOutlookChart's uncertainty cone uses),
// 90-day mark called out with a ReferenceLine.
// ---------------------------------------------------------------------------

const BORDER = 'hsl(var(--border, 240 5% 91%))';
const MUTED = 'hsl(var(--muted-foreground, 215 20% 65%))';

function RecoveryChart({ points }: { points: FinancialsOutlookResponse['recoverySeries'] }) {
  const rows = useMemo(
    () =>
      (points ?? []).map((p) => ({
        month: p.month,
        netRecoveryUsd: p.netRecoveryUsd,
        bookValueUsd: p.bookValueUsd,
        gap:
          p.bookValueUsd === null
            ? undefined
            : ([Math.min(p.netRecoveryUsd, p.bookValueUsd), Math.max(p.netRecoveryUsd, p.bookValueUsd)] as [
                number,
                number,
              ]),
      })),
    [points],
  );
  const ninetyDayMonth = rows[1]?.month ?? null;

  if (rows.length === 0) {
    return (
      <div className="flex h-[220px] items-center justify-center text-xs" style={{ color: MUTED }}>
        Not enough data to project recovery value yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[420px]">
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={rows} margin={{ top: 14, right: 12, bottom: 0, left: 4 }}>
            <CartesianGrid stroke={BORDER} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: MUTED }}
              tickLine={false}
              axisLine={{ stroke: BORDER }}
              tickFormatter={monthLong}
            />
            <YAxis
              tick={{ fontSize: 12, fill: MUTED }}
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={(v: number) => formatUsd(v, { compact: true })}
            />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 6,
                borderColor: BORDER,
                backgroundColor: 'hsl(var(--popover, 0 0% 100%))',
                color: 'hsl(var(--popover-foreground, 240 10% 4%))',
              }}
              labelFormatter={(m: string) => monthLong(m)}
              formatter={(value: number | [number, number], name: string) =>
                Array.isArray(value) ? [undefined, undefined] : [formatUsd(value), name]
              }
            />
            <Area dataKey="gap" name="Gap" fill={NET_RECOVERY_COLOR} fillOpacity={0.12} stroke="none" connectNulls legendType="none" />
            <Line
              dataKey="netRecoveryUsd"
              name="Projected net recovery"
              stroke={NET_RECOVERY_COLOR}
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              connectNulls
            />
            <Line
              dataKey="bookValueUsd"
              name="Book value"
              stroke={BOOK_VALUE_COLOR}
              strokeWidth={1.5}
              strokeDasharray="2 2"
              dot={false}
              connectNulls
            />
            {ninetyDayMonth && <ReferenceLine x={ninetyDayMonth} stroke={MUTED} strokeDasharray="3 3" label={{ value: '90d', position: 'insideTopRight', fontSize: 12, fill: MUTED }} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RecoveryLegend({ hasBookValue }: { hasBookValue: boolean }) {
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="h-0 w-3.5 border-t-2 border-dashed" style={{ borderColor: NET_RECOVERY_COLOR }} />
        Projected net recovery
      </span>
      {hasBookValue && (
        <span className="flex items-center gap-1.5">
          <span className="h-0 w-3.5 border-t-2 border-dashed" style={{ borderColor: BOOK_VALUE_COLOR }} />
          Book value
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state — coverage below the floor, in CoverageStatCard's voice.
// ---------------------------------------------------------------------------

function OutlookEmptyState({ coverage }: { coverage: FinancialsOutlookResponse['coverage'] }) {
  const pct = Math.round(coverage.pricedValuePct * 100);
  const floorPct = Math.round(FMV_OUTLOOK_COVERAGE_FLOOR_PCT * 100);
  return (
    <Card padding="md">
      <PanelTitle title="Outlook" description="Portfolio value trend, projection, and recovery curve" />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {coverage.pricedAssetCount === 0
            ? "Outlook needs FMV coverage on your portfolio — no assets are priced yet."
            : `Outlook needs FMV coverage on ${floorPct}% of portfolio value — currently ${pct}%.`}
        </p>
        <ImportFromErpLink className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline" />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Band
// ---------------------------------------------------------------------------

export function OutlookBand() {
  const outlookQ = useQuery<FinancialsOutlookResponse>({
    queryKey: qk.financials.outlook(),
    queryFn: async () => {
      const res = await api.api.client.financials.outlook.$get();
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as unknown as FinancialsOutlookResponse;
    },
  });

  if (outlookQ.isLoading) {
    return (
      <Card padding="md">
        <div className="text-sm text-muted-foreground">Loading outlook…</div>
      </Card>
    );
  }
  if (outlookQ.error || !outlookQ.data) {
    return null; // non-fatal — the rest of the screen still renders.
  }

  const data = outlookQ.data;

  if (data.projected90d === null || data.recoverySeries === null) {
    return <OutlookEmptyState coverage={data.coverage} />;
  }
  // Narrowed for TS below: both are non-null past the guard above.
  const projected90d = data.projected90d;
  const recoverySeries = data.recoverySeries;

  const spreadDays =
    data.asOf.latestSnapshotAt && data.asOf.oldestSnapshotAt
      ? Math.abs(new Date(data.asOf.latestSnapshotAt).getTime() - new Date(data.asOf.oldestSnapshotAt).getTime()) /
        (24 * 60 * 60 * 1000)
      : 0;

  return (
    <Card padding="md">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <PanelTitle title="Outlook" description="Portfolio value trend, projection, and recovery curve" />
        <div className="font-mono text-xs text-muted-foreground">
          as of {formatDate(data.asOf.latestSnapshotAt)}
          {spreadDays > 7 && data.asOf.oldestSnapshotAt && (
            <> · oldest input {formatDate(data.asOf.oldestSnapshotAt)}</>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <FmvTotalTile
          totalFmvMedUsd={data.totalFmvMedUsd}
          componentEstimatedAssetCount={data.componentEstimatedAssetCount}
          trend={data.trailingTrend}
        />
        <ProjectionTile projected90d={projected90d} />
        <ActionWindowTile refreshCliff={data.refreshCliff} />
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <RecoveryChart points={recoverySeries} />
        <RecoveryLegend hasBookValue={recoverySeries.some((p) => p.bookValueUsd !== null)} />
      </div>
    </Card>
  );
}
